import { createRouter, createWebHistory } from 'vue-router';
import { routes } from './routes';
import { useAuthStore } from '@/stores/auth';
import { useWorkspaceStore } from '@/stores/workspace';

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
});

/**
 * Guard de navegação.
 *
 * Ordem importa: a sessão precisa estar restaurada ANTES de decidir
 * qualquer redirecionamento, senão o usuário logado é jogado para /login
 * no primeiro carregamento — o bug clássico de SPA com auth assíncrona.
 *
 * Este guard é conveniência de UX, não segurança. Quem protege o dado é a
 * RLS no Postgres: mesmo que alguém force a rota, não há o que ler.
 */
router.beforeEach(async (to) => {
  const auth = useAuthStore();
  const workspace = useWorkspaceStore();

  await auth.initialize();

  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: 'dashboard' };
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  if (!auth.isAuthenticated) return true;

  // A partir daqui há sessão. Carrega workspaces uma vez.
  if (!workspace.loaded && !workspace.loading) {
    try {
      await workspace.initialize();
    } catch {
      // Falha de rede não pode travar a navegação: a página mostra o erro.
    }
  }

  const needsOnboarding = !auth.hasOnboarded || workspace.workspaces.length === 0;

  if (to.meta.requiresOnboarding && needsOnboarding) {
    return { name: 'onboarding' };
  }

  if (to.name === 'onboarding' && !needsOnboarding) {
    return { name: 'dashboard' };
  }

  /**
   * PAGOU, ATIVOU.
   *
   * A conta nasce sem assinatura. Sem pagamento confirmado não há produto:
   * cada mensagem do assistente custa token, e teste grátis aberto
   * significa pagar a conta de quem nunca teve intenção de assinar.
   *
   * Esta é a terceira camada da trava, e a mais fraca de propósito — é
   * conveniência de navegação. As que realmente seguram são o banco (a
   * conta nasce sem assinatura) e o servidor (`checkAiEntitlement` no
   * painel, `blockedReason` no WhatsApp). Forçar a URL não dá acesso a
   * nada: a RLS continua no lugar e o assistente recusa.
   *
   * `isSubscriptionActive` aceita TRIAL e GRACE além de ACTIVE. Não é
   * contradição: quem JÁ está em teste continua até o fim dele, e quem
   * está em carência de pagamento precisa de acesso para regularizar.
   * O que mudou foi parar de CONCEDER teste novo.
   */
  if (to.meta.requiresPayment && !workspace.isSubscriptionActive && !workspace.isPlatformAdmin) {
    return { name: 'signup' };
  }

  /**
   * O admin da PLATAFORMA não é cliente da plataforma.
   *
   * O workspace dele nasce sem assinatura como o de qualquer um, e o funil
   * o expulsava do próprio produto: login manda ao painel, painel exige
   * pagamento, funil recebe o dono do sistema pedindo cartão. Sem `/admin`
   * digitado na mão, não havia entrada.
   *
   * Isto libera NAVEGAÇÃO, não direito de consumo. O que custa dinheiro
   * continua barrado pelo servidor: `checkAiEntitlement` recusa qualquer
   * status fora de TRIAL/ACTIVE/GRACE, admin ou não. Para USAR o assistente
   * com a própria conta, o admin precisa de assinatura de verdade — a trava
   * de token não tem exceção, e não deve ter.
   *
   * A rota `/` continua aberta a ele de propósito: se o admin quiser
   * assinar como cliente, o funil precisa estar acessível.
   */

  // O contrário: quem já pode operar não fica preso no funil.
  if (to.meta.funnel && workspace.isSubscriptionActive) {
    return { name: 'dashboard' };
  }

  // Admin da PLATAFORMA — nunca confundido com OWNER de workspace.
  // A checagem real acontece no servidor: as RPCs de admin verificam
  // app.is_platform_admin() e recusam quem não for.
  if (to.meta.requiresAdmin && !workspace.isPlatformAdmin) {
    return { name: 'dashboard' };
  }

  return true;
});
